import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useFormContext } from 'react-hook-form'

import useTranslate from '../../../lib/hooks/use-translate'
import {
  TaughtLevelEnum,
  taughtLevelEnumMapping
} from '../../../lib/types/enums/common/taught-level.enum'
import { TeflInput } from '../../../lib/types/models/tefl/tefl-input'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'

const confirmationResponses = [
  {
    label: 'Yes',
    value: true
  },
  {
    label: 'No',
    value: false
  }
]

const TeflTeachExperienceForm = () => {
  const form = useFormContext<TeflInput>()
  const { t } = useTranslate()
  const { ableToTeachInAnotherArea } = form.watch()

  return (
    <InnerBox>
      <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
        Teaching Experience and TEFL
      </Typography>
      <div className="h-4" />

      <div className="flex flex-col gap-3">
        <FormField
          control={form.control}
          name="firstYearOfTeaching"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput
                  {...field}
                  required
                  label={'In what year did you start teaching?'}
                  placeholder={'Year'}
                  type={'number'}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="mostLevelTaught"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  required
                  label="Most taught levels"
                  options={Object.values(TaughtLevelEnum).map((level) => ({
                    label: t(taughtLevelEnumMapping[level]),
                    value: level
                  }))}
                  selectedOption={getSelectedEnumOption(field.value, taughtLevelEnumMapping, t)}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex items-center justify-between pr-14">
          <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
            Capable of teaching in another field *
          </Typography>
          <div className="h-2" />
          <div className="flex gap-4">
            {confirmationResponses.map((response) => (
              <FormField
                key={response.label}
                control={form.control}
                name="ableToTeachInAnotherArea"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <ControlledRadioInput
                          {...field}
                          label={response.label}
                          checked={field.value === response.value}
                          onCheck={() => field.onChange(response.value)}
                        />
                      </FormControl>
                    </FormItem>
                  )
                }}
              />
            ))}
          </div>
        </div>

        {ableToTeachInAnotherArea && (
          <div>
            <FormField
              control={form.control}
              name="fieldOfExpertise"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <ControlledInput
                      {...field}
                      required
                      label={'Specify which field?'}
                      type={'text'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        )}

        <div className="flex items-center justify-between pr-14">
          <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
            Driver's license *
          </Typography>
          <div className="h-2" />
          <div className="flex gap-4">
            {confirmationResponses.map((response) => (
              <FormField
                key={response.label}
                control={form.control}
                name="drivingLicense"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <ControlledRadioInput
                          {...field}
                          label={response.label}
                          checked={field.value === response.value}
                          onCheck={() => field.onChange(response.value)}
                        />
                      </FormControl>
                    </FormItem>
                  )
                }}
              />
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between pr-14">
          <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
            Car *
          </Typography>
          <div className="h-2" />
          <div className="flex gap-4">
            {confirmationResponses.map((response) => (
              <FormField
                key={response.label}
                control={form.control}
                name="car"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <ControlledRadioInput
                          {...field}
                          label={response.label}
                          checked={field.value === response.value}
                          onCheck={() => field.onChange(response.value)}
                        />
                      </FormControl>
                    </FormItem>
                  )
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </InnerBox>
  )
}

export default TeflTeachExperienceForm
